<template>
  <div class="login-container">
    <div class="left-section">
      <video
        class="hero-video"
        :key="heroSrc"
        :src="heroSrc"
        :poster="heroPosterSrc"
        autoplay
        muted
        loop
        playsinline
      ></video>
      <div class="logo">
        <img :src="logoWhite" alt="Peelo Logo" />
      </div>
      <a href="https://peelo.io" class="back-link">
        <span>{{ $t('common.backToSite') }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </a>
      <div class="hero-content">
        <TransitionGroup name="fade-slide">
          <div v-for="(slide, index) in slides" :key="'slide-' + index" v-show="currentSlide === index" class="slide">
            <h2>{{ slide.title }}</h2>
            <p>{{ slide.description }}</p>
          </div>
        </TransitionGroup>
        <div class="dots">
          <span 
            v-for="(slide, index) in slides" 
            :key="'dot-' + index"
            :class="['dot', { active: currentSlideIndex === index }]"
            @click="setSlide(index)"
          ></span>
        </div>
      </div>
    </div>
    <div class="right-section">
      <div class="auth-container">
        <div class="back-button-container">
          <router-link to="/login" class="back-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>{{ $t('common.back') }}</span>
          </router-link>
        </div>
        <h1>{{ $t('forgotPassword.title') }}</h1>
        <p class="login-text">
          {{ $t('forgotPassword.subtitle') }}
        </p>
        
        <div v-if="!resetLinkSent">
          <form @submit.prevent="sendResetLink" class="forgot-password-form">
            <div class="form-group">
              <label for="email">{{ $t('auth.email') }}</label>
              <input 
                type="email" 
                id="email" 
                v-model="email" 
                :placeholder="$t('auth.emailPlaceholder')"
                required
                :class="{ 'input-error': errorMessage }"
              />
            </div>
            
            <div v-if="errorMessage" class="error-message">
              {{ errorMessage }}
            </div>
            
            <button type="submit" class="submit-button" :disabled="isLoading">
              <span v-if="isLoading" class="loader"></span>
              <span v-else>{{ $t('forgotPassword.sendLink') }}</span>
            </button>
          </form>
        </div>
        
        <div v-else class="success-container">
          <div class="success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h3 class="success-title">{{ $t('forgotPassword.emailSent') }}</h3>
          <p class="success-text">
            {{ $t('forgotPassword.emailSentMessage') }}
            <br><br>
            <strong>{{ $t('forgotPassword.checkSpam') }}</strong>
          </p>
          <button @click="resetForm" class="back-to-login-button">
            {{ $t('forgotPassword.backToLogin') }}
          </button>
        </div>

        <div class="auth-footer">
          <div class="separator">
            <span>{{ $t('common.or') }}</span>
          </div>
          
          <div class="login-prompt">
            <span>{{ $t('auth.knowYourPassword') }}</span>
            <router-link to="/login" class="login-button">
              {{ $t('auth.loginButton') }}
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import logoWhite from '@/assets/logo-white.png';
import heroVideo from '@/assets/hero-3d.mp4';
import heroVideoMobile from '@/assets/hero-3d-mobile.mp4';
import heroPoster from '@/assets/hero-3d-poster.webp';
import heroPosterMobile from '@/assets/hero-3d-poster-mobile.webp';
import { useI18n } from 'vue-i18n';
import { axiosPeelo } from '@/utils/axios';

export default {
  name: 'ForgotPassword',
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();
    const currentSlideIndex = ref(0);
    const currentSlide = ref(0);
    const isTransitioning = ref(false);
    const isLoading = ref(false);
    const errorMessage = ref('');
    const email = ref('');
    const resetLinkSent = ref(false);

    // Vidéo hero : 9:16 sur desktop, 16:9 sur mobile (même breakpoint que le CSS)
    const heroMediaQuery = window.matchMedia('(max-width: 768px)');
    const isMobileHero = ref(heroMediaQuery.matches);
    const onHeroMediaChange = (e) => { isMobileHero.value = e.matches; };
    const heroSrc = computed(() => (isMobileHero.value ? heroVideoMobile : heroVideo));
    const heroPosterSrc = computed(() => (isMobileHero.value ? heroPosterMobile : heroPoster));

    const slides = computed(() => [
      {
        title: t('slides.slide1.title'),
        description: t('slides.slide1.description')
      },
      {
        title: t('slides.slide2.title'),
        description: t('slides.slide2.description')
      },
      {
        title: t('slides.slide3.title'),
        description: t('slides.slide3.description')
      }
    ]);

    let slideInterval;

    const setSlide = (index) => {
      if (isTransitioning.value) return;
      
      isTransitioning.value = true;
      currentSlideIndex.value = index;
      
      setTimeout(() => {
        currentSlide.value = index;
        isTransitioning.value = false;
      }, 300);
    };

    const startSlideShow = () => {
      slideInterval = setInterval(() => {
        const nextIndex = (currentSlideIndex.value + 1) % slides.value.length;
        setSlide(nextIndex);
      }, 5000);
    };

    const sendResetLink = async () => {
      if (!email.value) {
        errorMessage.value = t('auth.errors.enterEmail');
        return;
      }
      
      isLoading.value = true;
      errorMessage.value = '';
      
      try {
        const currentLocale = locale.value;
        console.log('📧 Sending reset password request with locale:', currentLocale);
        
        const response = await axiosPeelo.post('/strong/auth/reset-password', { 
          email: email.value,
          locale: currentLocale
        });
        
        if (response.data && response.data.success) {
          resetLinkSent.value = true;
          isLoading.value = false;
        } else {
          throw new Error(response.data?.message || t('auth.errors.resetLinkError'));
        }
      } catch (error) {
        isLoading.value = false;
        
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage.value = error.response.data.message;
        } else {
          errorMessage.value = t('auth.errors.resetError');
        }
      }
    };
    
    const resetForm = () => {
      router.push('/login');
    };

    onMounted(() => {
      heroMediaQuery.addEventListener('change', onHeroMediaChange);
      startSlideShow();
    });

    onBeforeUnmount(() => {
      heroMediaQuery.removeEventListener('change', onHeroMediaChange);
      clearInterval(slideInterval);
    });

    return {
      currentSlideIndex,
      currentSlide,
      isTransitioning,
      slides,
      setSlide,
      isLoading,
      errorMessage,
      email,
      sendResetLink,
      resetLinkSent,
      logoWhite,
      heroSrc,
      heroPosterSrc,
      resetForm
    };
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: linear-gradient(160deg, var(--dark-bg) 0%, var(--dark-bg-deep) 100%);
  overflow: hidden;
}

.left-section {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  margin: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 2rem);
  max-height: calc(100vh - 2rem);
}

/* Sélecteur doublé pour battre la règle `.left-section > *` (position/z-index) */
.left-section .hero-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;
  z-index: 0;
}

.left-section:hover .hero-video {
  transform: scale(1.08);
}

.left-section::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(37, 211, 102, 0.6), rgba(37, 211, 102, 0.2));
  z-index: 1;
}

.left-section > * {
  position: relative;
  z-index: 2;
}

.logo {
  position: relative;
  margin-bottom: 2rem;
}

.logo img {
  height: 40px;
  width: auto;
}

.back-link {
  position: absolute;
  top: 2rem;
  right: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(18, 140, 126, 0.2);
  border-radius: 20px;
  font-size: 14px;
  transition: background-color 0.2s;
}

.back-link:hover {
  background: rgba(18, 140, 126, 0.3);
}

.hero-content {
  margin-top: auto;
  margin-bottom: 3rem;
  color: white;
  position: relative;
  height: auto;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: visible;
}

.slide {
  padding: 0 3rem;
  position: absolute;
  width: 100%;
  overflow: visible;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.slide h2 {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.slide p {
  font-size: 1.2rem;
  line-height: 1.5;
  opacity: 0.9;
  margin-bottom: 2rem;
}

.dots {
  display: flex;
  gap: 0.5rem;
  padding: 0 3rem;
  margin-top: 6rem;
  justify-content: center;
}

.dot {
  width: 2.5rem;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.dot:hover {
  background: rgba(255, 255, 255, 0.4);
}

.dot.active {
  background: white;
  width: 4rem;
}

.right-section {
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  overflow-x: hidden;
}

.auth-container {
  max-width: 400px;
  width: 100%;
  overflow: hidden;
}

h1 {
  color: white;
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  color: white;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 1rem;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.input-error {
  border-color: #ff5252 !important;
}

.error-message {
  color: #ff5252;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background-color: rgba(255, 82, 82, 0.1);
  border-radius: 8px;
  border-left: 3px solid #ff5252;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.submit-button {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: var(--dark-bg);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.submit-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.submit-button:active {
  transform: translateY(0);
}

.submit-button:disabled {
  background: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(20, 48, 40, 0.3);
  border-radius: 50%;
  border-top-color: var(--dark-bg);
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.auth-footer {
  margin-top: 2rem;
}

.separator {
  display: flex;
  align-items: center;
  text-align: center;
  margin: 2rem 0;
  color: #8f8f8f;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.separator span {
  padding: 0 10px;
}

.login-prompt {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.login-prompt span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

.login-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 8px;
  color: #25D366;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.login-button:hover {
  background: rgba(37, 211, 102, 0.2);
  transform: translateX(5px);
}

.login-button svg {
  transition: transform 0.2s ease;
}

.login-button:hover svg {
  transform: translateX(3px);
}

.forgot-password-form {
  margin-top: 1rem;
}

.success-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 0;
  animation: fadeIn 0.5s ease;
}

.success-icon {
  margin-bottom: 1.5rem;
}

.success-title {
  color: #25D366;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.success-text {
  color: #8f8f8f;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.back-to-login-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: white;
  color: var(--dark-bg);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.back-to-login-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.back-button-container {
  margin-bottom: 1.5rem;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  padding: 0.5rem 0;
}

.back-button:hover {
  color: white;
  transform: translateX(-5px);
}

.back-button svg {
  transition: transform 0.2s ease;
}

.back-button:hover svg {
  transform: translateX(-3px);
}

.fade-slide-enter-active {
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
}

.fade-slide-leave-active {
  transition: all 0.6s ease-in;
  position: absolute;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

@media (max-width: 768px) {
  .login-container {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  /* Le formulaire d'abord : l'utilisateur voit les champs sans scroller */
  .right-section {
    order: 1;
    padding: 1.5rem 1.25rem 2rem;
    min-height: auto;
  }

  /* La vidéo devient une bannière compacte sous le formulaire */
  .left-section {
    order: 2;
    min-height: 220px;
    max-height: 260px;
    margin: 0.75rem;
    padding: 1.25rem;
  }

  .logo img {
    height: 32px;
  }

  .back-link {
    top: 1rem;
    right: 1rem;
    padding: 0.35rem 0.75rem;
    font-size: 13px;
  }

  .hero-content {
    margin-bottom: 0.5rem;
    min-height: 110px;
  }

  .auth-container {
    margin: 0 auto;
    padding: 0 1rem;
  }

  h1 {
    font-size: 2rem;
  }

  .slide {
    padding: 0;
  }

  .slide h2 {
    font-size: 1.4rem;
  }

  .slide p {
    font-size: 0.9rem;
  }

  .dots {
    padding: 0;
    margin-top: 1.5rem;
  }
}
</style>
